import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowDown, Lock } from "lucide-react";

const easeOut = [0.25, 0.4, 0.25, 1];

const EVENTS = [
  { id: "shooting-1109", date: "11.09", day: "11 septembre 2026", private: true },
  { id: "culturisme", date: "12.09", day: "12 septembre 2026", title: "Championnat de Culturisme", type: "bodybuilding" },
  { id: "wbc-youth", date: "17–19.09", day: "17–19 septembre 2026", title: "Championnats du Monde WBC Youth", place: "Metz", type: "boxing", status: "accredited", link: "/wbc-youth" },
  { id: "pfw", date: "Fin 09", day: "Fin septembre 2026", title: "Paris Fashion Week", place: "Paris", type: "fashion", status: "pending" },
  { id: "ultimate-cage", date: "24.10", day: "24 octobre 2026", title: "Ultimate Cage VII", type: "mma", status: "accredited" },
  { id: "wbc-francophone", date: "31.10", day: "31 octobre 2026", title: "Gala WBC Francophone", place: "Les Sables-d'Olonne", type: "boxing", status: "pending" },
  { id: "nuit-combattants", date: "31.10", day: "31 octobre 2026", title: "La Nuit des Combattants", place: "Rouen", type: "boxing", status: "pending" },
];

const STATUS_STYLE = {
  accredited: "border-leaf bg-leaf text-bone",
  pending: "border-bone/30 text-bone/75 qr-pulse",
  tbc: "border-ash/40 text-ash",
};

function EventCard({ ev, i, t }) {
  const status = ev.status || "tbc";
  const Wrapper = ev.link ? motion.a : motion.article;
  return (
    <Wrapper
      {...(ev.link ? { href: ev.link } : {})}
      data-testid={`agenda-card-${ev.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: easeOut }}
      className={`group relative flex flex-col justify-between border p-7 md:p-9 min-h-[240px] transition-colors duration-500 ${
        ev.private ? "border-line bg-surface/20" : "border-line bg-surface/30 hover:border-moss"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <p className={`font-display text-4xl md:text-5xl leading-none ${ev.private ? "text-bone/25" : "text-bone/80 group-hover:text-leaf transition-colors"}`}>
          {ev.date}
        </p>
        {ev.private ? (
          <Lock size={18} strokeWidth={1.4} className="text-ash/50" />
        ) : (
          <span
            data-testid={`agenda-status-${ev.id}`}
            className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border whitespace-nowrap ${STATUS_STYLE[status]}`}
          >
            {t.agenda.status[status]}
          </span>
        )}
      </div>

      <div className="mt-8">
        <p className="text-[10px] tracking-[0.35em] uppercase text-ash">{ev.day}</p>
        {ev.private ? (
          <h3 className="mt-3 font-serif text-2xl md:text-3xl text-bone/60 italic">{t.agenda.private}</h3>
        ) : (
          <>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl text-bone leading-tight">{ev.title}</h3>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] tracking-[0.3em] uppercase text-ash">
              <span>{t.agenda.types[ev.type]}</span>
              {ev.place && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={11} strokeWidth={1.5} className="text-leaf" /> {ev.place}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
}

export default function Agenda({ t }) {
  const book = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="agenda" data-testid="agenda-section" className="relative bg-ink border-t border-line py-24 md:py-36">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="block h-px w-10 bg-leaf"></span>
              <p className="text-[11px] tracking-[0.4em] uppercase text-ash">{t.agenda.eyebrow}</p>
            </div>
            <h2
              data-testid="agenda-title"
              className="font-serif font-light text-bone text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight"
            >
              {t.agenda.title}
              <br />
              <span className="italic">{t.agenda.title2}</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-ash text-base leading-relaxed max-w-md">{t.agenda.intro}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {EVENTS.map((ev, i) => (
            <EventCard key={ev.id} ev={ev} i={i} t={t} />
          ))}
        </div>

        <div className="mt-16 md:mt-24 border-t border-line pt-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <p className="font-serif italic text-bone text-xl md:text-2xl max-w-lg">{t.agenda.ctaText}</p>
          <button
            data-testid="agenda-book-btn"
            onClick={book}
            className="group inline-flex items-center gap-3 border border-bone/40 hover:border-leaf hover:bg-leaf/10 transition-all duration-500 px-7 py-4 self-start"
          >
            <span className="text-[11px] tracking-[0.32em] uppercase text-bone">{t.agenda.cta}</span>
            <ArrowDown size={15} strokeWidth={1.5} className="text-leaf group-hover:translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
